import { Progress as BaseProgress } from '@base-ui/react/progress'
import React from 'react'
import styled, { keyframes } from 'styled-components'
import { gray, semantic } from 'sutra/tokens/colors'
import { motion } from 'sutra/tokens/motion'

export interface ProgressProps {
  // 0 to 100. `null` is indeterminate: no `aria-valuenow`, and the bar sweeps
  // instead of filling. Out-of-range values are clamped, not rejected.
  value: number | null
  // Accessible name. A bar has no text of its own, so it is required.
  label: string
  // Fill color; defaults to the accent. Lets a call site color by level.
  color?: string
  // Width is the call site's: the bar fills its container (`styled(Progress)`).
  className?: string
}

const BAR_HEIGHT = 6

const sweep = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(250%); }
`

const Track = styled(BaseProgress.Track)`
  position: relative;
  box-sizing: border-box;
  width: 100%;
  height: ${BAR_HEIGHT}px;
  overflow: hidden;
  border-radius: 9999px;
  background: ${gray[200]};
`

const Indicator = styled(BaseProgress.Indicator)<{ $color: string }>`
  display: block;
  border-radius: 9999px;
  background: ${({ $color }) => $color};
  transition: width ${motion.duration.fast}ms ${motion.ease};

  &[data-indeterminate] {
    width: 40%;
    height: 100%;
    transition: none;
    animation: ${sweep} 1.2s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    &[data-indeterminate] {
      animation: none;
      width: 100%;
      opacity: 0.4;
    }
  }
`

export const Progress = ({
  value,
  label,
  color = semantic.accent,
  className,
}: ProgressProps): React.ReactElement => (
  <BaseProgress.Root value={value} aria-label={label} className={className}>
    <Track>
      <Indicator $color={color} />
    </Track>
  </BaseProgress.Root>
)

Progress.displayName = 'Progress'
